// Port of pension_report/calc.py
// Pure financial math — no side effects, no DOM access.
import { CFG } from './config.js';

export const SCENARIOS = [
  { key: 'tax_advantaged', name: '절세계좌(연금저축/IRP 가정)', is_tax_advantaged: true },
  { key: 'taxable',        name: '일반계좌(과세 가정)',          is_tax_advantaged: false },
];

function pensionTaxRateByAge(withdrawalStartAge) {
  if (withdrawalStartAge >= 80) return CFG.PENSION_TAX_80_PLUS;
  if (withdrawalStartAge >= 70) return CFG.PENSION_TAX_70_79;
  return CFG.PENSION_TAX_55_69;
}

// Annual effective rate → monthly effective rate
function effectiveMonthlyRate(annualRate) {
  return Math.pow(1.0 + annualRate, 1.0 / 12.0) - 1.0;
}

// Future value factor of an ordinary annuity of 1/month for nMonths at rM/month
function annuityFvFactor(rM, nMonths) {
  if (nMonths <= 0) return 0.0;
  if (Math.abs(rM) < 1e-12) return nMonths;
  return (Math.pow(1.0 + rM, nMonths) - 1.0) / rM;
}

function computeScenario(customer, scenario) {
  const yearsToRetire  = Math.trunc(customer.retirement_start_age - customer.current_age);
  const monthsToRetire = yearsToRetire * 12;

  // 1) Cashflow gap: 목표 월 연금 - 국민연금
  const neededMonthly = Math.max(0, customer.target_monthly_pension - customer.expected_nps_monthly);
  const neededAnnual  = neededMonthly * 12;

  // 2) 시나리오별 세율·수수료
  const annualFee = CFG.ANNUAL_MANAGEMENT_FEE;
  const tradeFee  = CFG.APPLY_TRADE_FEE_TO_MONTHLY ? CFG.TRADE_FEE_RATE : 0;

  let withdrawTax;
  let accumulationNet;

  if (scenario.is_tax_advantaged) {
    // 절세계좌: 적립 기간은 세금 유예, 인출 시 연금소득세
    withdrawTax     = pensionTaxRateByAge(customer.retirement_start_age);
    accumulationNet = customer.accumulation_target_return - annualFee;
  } else {
    // 일반계좌: 매년 이자/배당에 원천징수세 부과
    withdrawTax     = CFG.TAX_INTEREST_DIVIDEND;
    accumulationNet = (customer.accumulation_target_return - annualFee) * (1 - CFG.TAX_INTEREST_DIVIDEND);
  }
  accumulationNet = Math.max(-0.95, accumulationNet);

  const withdrawalNetYield = Math.max(0, customer.withdrawal_target_yield - annualFee);

  // 3) 필요 은퇴 자산 (원금 유지, 이자만 인출 가정)
  let grossNeededAnnual;
  if (CFG.TARGET_IS_AFTER_TAX) {
    const afterTaxFactor = 1 - withdrawTax;
    grossNeededAnnual = afterTaxFactor <= 1e-9 ? Infinity : neededAnnual / afterTaxFactor;
  } else {
    grossNeededAnnual = neededAnnual;
  }

  const requiredAsset = withdrawalNetYield <= 1e-12
    ? (grossNeededAnnual > 0 ? Infinity : 0)
    : grossNeededAnnual / withdrawalNetYield;

  // 4) 기존 자산(개인연금 + 퇴직연금)의 은퇴 시점 미래가치
  const pvExisting = customer.current_personal_pension + customer.current_retirement_pension;
  const fvExisting = pvExisting * Math.pow(1 + accumulationNet, yearsToRetire);
  const fvGap      = Math.max(0, requiredAsset - fvExisting);

  // 5) 갭을 채우기 위한 월 납입금 역산
  const rM       = effectiveMonthlyRate(accumulationNet);
  const fvFactor = annuityFvFactor(rM, monthsToRetire);

  let requiredMonthlyInvested;
  if (fvFactor <= 0 || !Number.isFinite(requiredAsset)) {
    requiredMonthlyInvested = fvGap > 0 ? Infinity : 0;
  } else {
    requiredMonthlyInvested = fvGap / fvFactor;
  }

  // 거래 수수료 공제 전 납입 필요 금액
  const requiredMonthly = (1 - tradeFee) <= 1e-9
    ? Infinity
    : requiredMonthlyInvested / (1 - tradeFee);

  const totalContributed = Number.isFinite(requiredMonthly)
    ? requiredMonthly * monthsToRetire
    : Infinity;

  // 6) 연도별 자산 성장 투영 (기준 / 보수(-2%p) / 낙관(+2%p))
  const projectionYears = Array.from({ length: yearsToRetire + 1 }, (_, i) => i);
  const base = accumulationNet;
  const cons = Math.max(-0.95, base - 0.02);
  const opt  = base + 0.02;

  function assetAtYear(tYears, annualNet) {
    if (tYears <= 0) return pvExisting;
    const rm      = effectiveMonthlyRate(annualNet);
    const n       = tYears * 12;
    const fvExist = pvExisting * Math.pow(1 + annualNet, tYears);
    const fvPmt   = requiredMonthlyInvested * annuityFvFactor(rm, n);
    return fvExist + fvPmt;
  }

  return {
    scenario_key:                   scenario.key,
    scenario_name:                  scenario.name,
    years_to_retire:                yearsToRetire,
    months_to_retire:               monthsToRetire,
    needed_monthly_cashflow:        neededMonthly,
    needed_annual_cashflow:         neededAnnual,
    withdrawal_tax_rate:            withdrawTax,
    accumulation_net_return:        accumulationNet,
    withdrawal_net_yield:           withdrawalNetYield,
    required_retirement_asset:      requiredAsset,
    fv_existing_at_retirement:      fvExisting,
    fv_gap:                         fvGap,
    required_monthly_investment:    requiredMonthly,
    required_monthly_invested:      requiredMonthlyInvested,
    total_contributed:              totalContributed,
    projection_years:               projectionYears,
    projection_assets_base:         projectionYears.map(t => assetAtYear(t, base)),
    projection_assets_conservative: projectionYears.map(t => assetAtYear(t, cons)),
    projection_assets_optimistic:   projectionYears.map(t => assetAtYear(t, opt)),
  };
}

export function computeAll(customer) {
  return SCENARIOS.map(sc => computeScenario(customer, sc));
}
